/**
 * 这个工具主要的是处理一般情况的方法
 */

import qs from 'qs';
import _ from 'lodash';
import shallowEqual from 'shallowequal';
import ABSMessage from '../components/ABSMessage';
import { GlobalApi } from '../abs/config/api';
import { absDiv, absMul } from './calculationUtils';

const defaultAvatar = require('../assets/images/default-avatar.png');
class CommonUtils {
  nullText = '--';

  isPlus = 0;

  isMobile = {
    Android: () => {
      return navigator.userAgent.match(/Android/i);
    },
    BlackBerry: () => {
      return navigator.userAgent.match(/BlackBerry/i);
    },
    iOS: () => {
      return navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    Opera: () => {
      return navigator.userAgent.match(/Opera Mini/i);
    },
    Windows: () => {
      return navigator.userAgent.match(/IEMobile/i) || navigator.userAgent.match(/WPDesktop/i);
    },
    any: () => {
      return (
        this.isMobile.Android() ||
        this.isMobile.BlackBerry() ||
        this.isMobile.iOS() ||
        this.isMobile.Opera() ||
        this.isMobile.Windows()
      );
    }
  };

  /**
   * 获取电脑系统是否是苹果
   */
  isMac = () => {
    return /macintosh|mac os x/i.test(navigator.userAgent);
  };

  /**
   * 获取浏览器的类型
   * @param name 小写的名称
   */
  getBrowser(name?: string) {
    return navigator.userAgent.toLowerCase().indexOf(name || 'firefox') > 0;
  }

  /**
   * 深拷贝
   */
  deep(data: any) {
    return _.cloneDeep(data);
  }

  /**
   * 一个剔除所有给定值的新数组
   * @param arr   原数组
   * @param values 待剔除的定值
   */
  without(arr: any[], values: any[]) {
    return _.without(arr, ...values);
  }

  /**
   * 返回新的去重后的数组
   * @param data
   * @param isSort 是否重新排序
   */
  uniqArray(data: any, isSort?: boolean) {
    if (isSort) {
      return _.sortedUniq(data);
    }
    return _.uniq(data);
  }

  /**
   * 返回最大的值
   * @param array 要迭代的数组。
   * @param iteratee 调用每个元素的迭代函数
   */
  maxBy(array: any, iteratee: any) {
    return _.maxBy(array, iteratee);
  }

  /**
   * 返回匹配元素，否则返回 undefined
   * @param collection 一个用来迭代的集合
   * @param predicate (Array|Function|Object|string): 每次迭代调用的函数。
   * @param fromIndex 开始搜索的索引位置
   */
  find(collection: any, predicate: any, fromIndex?: number) {
    return _.find(collection, predicate, fromIndex);
  }

  /**
   * 获取对象的值
   * @param value
   * @param key
   * @param value
   */
  getValue(object: any, key: string, value?: any) {
    return _.get(object, key, value);
  }

  /**
   * 设置对象的值
   * @param value
   * @param key
   * @param value
   */
  setValue(object: any, key: string, value: any) {
    return _.set(object, key, value);
  }

  /**
   * 比较两个数据是否相等
   * @param prev
   * @param next
   */
  isEqual(prev: any, next: any, isDeep?: boolean) {
    if (!isDeep) {
      return shallowEqual(prev, next);
    }
    return _.isEqual(prev, next);
  }

  /**
   * 检查 value 是否为一个空对象，集合，映射或者set
   * 如果 value 为空，那么返回 true，否则返回 false。
   * @param value
   */
  isEmpty(value: any) {
    return _.isEmpty(value);
  }

  /**
   * 检查 valuealue 是否是 null 或者是undefined
   * @param value
   */
  isNullOrUndefined(value: any) {
    const isNull = _.isNull(value);
    const isUndefined = _.isUndefined(value);
    return isNull || isUndefined || false;
  }

  /**
   * 返回过滤掉假值的新数组。
   * @param array 待处理的数组
   * _.compact([0, 1, false, 2, '', 3]);
   */
  compact(array: any[]) {
    return _.compact(array);
  }

  /**
   * 如果找到 value 返回 true， 否则返回 false
   * @param collection
   * @param value  要检索的值。
   * @param fromIndex   要检索的 索引位置
   *
   * _.includes([1, 2, 3], 1);   true
   * _.includes([1, 2, 3], 1, 2); false
   * _.includes({ 'user': 'fred', 'age': 40 }, 'fred'); true
   */
  includes(collection: any, value: any, fromIndex?: number) {
    return _.includes(collection, value, fromIndex);
  }

  /**
   *
   * func (Function): 要防抖动的函数。
   * [wait=0] (number): 需要延迟的毫秒数。
   * [options=] (Object): 选项对象。
   * [options.leading=false] (boolean): 指定在延迟开始前调用。
   * [options.maxWait] (number): 设置 func 允许被延迟的最大值。
   * [options.trailing=true] (boolean): 指定在延迟结束后调用。
   */
  debounce(func: any, wait?: number, options?: any) {
    return _.debounce(func, wait, options);
  }

  /**
   * 返回节流的函数。
   * func (Function): 要节流的函数。
   * [wait=0] (number): 需要延迟的毫秒数。
   * [options=] (Object): 选项对象。
   * [options.leading=false] (boolean): 指定在延迟开始前调用。
   * [options.maxWait] (number): 设置 func 允许被延迟的最大值。
   * [options.trailing=true] (boolean): 指定在延迟结束后调用。
   */
  throttle(func: any, wait?: number, options?: any) {
    return _.throttle(func, wait, options);
  }

  /**
   * 排序排序后的新数组
   * @param collection 用来迭代的集合
   * @param iteratees (Array[]|Function[]|Object[]|string[]): 排序的迭代函数。
   * @param orders iteratees   迭代函数的排序顺序 ['asc', 'desc']
   *
   * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
   */
  orderBy(collection: any, iteratees: any[], orders: any[]) {
    return _.orderBy(collection, iteratees, orders);
  }

  /**
   * 返回排序后的数组
   * @param collection (Array[]|Function[]|Object[]|string[]) 用来迭代的集合
   * @param iteratees  这个函数决定排序
   *
   * _.sortBy(users, ['user', 'age']);
   * _.sortBy(users, function(o) { return o.user; });
   */
  sortBy(collection: any, iteratees?: any) {
    return _.sortBy(collection, iteratees);
  }

  /**
   * 判断是否是空
   * @param value
   */
  isNull(value: any) {
    return _.isNull(value);
  }

  /**
   * 检查 value 是否是 undefined.
   * @param value
   */
  isUndefined(value: any) {
    return _.isUndefined(value);
  }

  /**
   * 移除属性
   * @param object 对象
   * @param props  移除的属性
   */
  omit(object: any, props: any[]) {
    return _.omit(object, props);
  }

  /**
   * 数据格式综合处理
   * @param contents 内容
   * @param isTable 是否为null是，数据来源类型是否是table (默认：false) null=false
   * @param isPercent 是否*100（百分位） (默认：false) null=false
   * @param isThousandth 是否有千分位+‘,’  (默认：true) null=true
   * @param num 保留位数 （默认：不填时为2；如果不保留位数请填0）null=2
   * @param unit 数值转换,单位：（默认：0=【元】；4=【万】；6=【百万】；8=【亿】）null=0
   * @param showPercentSign 是否显示百分号 （默认: true)
   */
  formatContent = (
    contents: any,
    isTable?: boolean | null,
    isPercent?: boolean | null,
    isThousandth?: boolean | null,
    num?: number | null,
    unit?: 0 | 4 | 6 | 8 | null,
    showPercentage?: boolean | null
  ) => {
    if ((contents || contents === 0) && contents.toString().length > 0) {
      let contentsNum = this.isNumber(contents);
      if (contentsNum || contentsNum === 0) {
        if (isPercent) {
          contentsNum = absMul(contentsNum, 100);
        }
        if (unit) {
          const pow = Math.pow(10, unit);
          contentsNum = absDiv(contentsNum, pow);
        }
        contentsNum = this.toFixed(contentsNum, num);
        if (isThousandth !== false ? true : isThousandth) {
          contentsNum = this.formatCurrency(contentsNum);
        }

        // 不传默认值为true
        const shouldShowPercentage = showPercentage == null ? true : showPercentage;
        // 没有传num 保留%  传了num 且 num <=2 保留%
        const showPercent = !num || num <= 2 || num === 4;
        if (isPercent && showPercent && shouldShowPercentage) {
          return `${contentsNum}%`;
        }
        return contentsNum;
      }
      return contents;
    }
    const nullText = this.nullText;
    return nullText;
  };

  /**
   * 精确度的控制
   * @param num 转换的数字
   * @param position 显示几位小数，默认的是两位小数，可以不传
   */
  toFixed(num: any, position?: number | null) {
    const positions = !position && position !== 0 ? 2 : position;
    return num.toFixed(positions);
  }

  /**
   * 添加符号例如%
   * @param val 待处理值
   * @param symbol 添加符号, 默认%
   * @param fixedNum 保留小数位数
   */
  addSign(val: any, symbol: string, fixedNum: number) {
    if (!val && val !== 0) {
      return this.nullText;
    }
    if (fixedNum) {
      return Number(val).toFixed(fixedNum) + (symbol || '%');
    }
    return val + (symbol || '%');
  }

  /**
   * 获取对象的值
   * @param value
   * @param key
   * @param value
   */
  hasValue(object: any, path: string) {
    return _.has(object, path);
  }

  /**
   * 内容是否为数字处理
   * @param contents 内容
   * @param isInteger 是否是整数
   */
  isNumber(contents: any, isInteger?: boolean) {
    if (!isNaN(contents) && typeof contents === 'number') {
      return contents;
    }
    if (typeof contents === 'string' && contents.trim() !== '') {
      if (Number.isFinite ? Number.isFinite(+contents) : isFinite(+contents)) {
        return Number(contents);
      }
    }
    if (isInteger) {
      return _.isInteger(contents);
    }
    return false;
  }

  /**
   * 获取头像地址，头像不存在时使用默认头像
   * @param {string} avatar  原头像
   * @returns {string} 新头像
   */
  getAvatar(avatar?: string | null): string {
    return avatar ? avatar : defaultAvatar;
  }

  /**
   * 获取请求中hash的值
   */
  getParams() {
    return qs.parse(location.hash.split('?')[1]);
  }

  /**
   * 转换Url（域名替换）
   * @param {string} url  原链接
   * @returns {string} 新链接
   */
  parseUrl(url: string): string {
    if (!url) {
      return url;
    }
    return url.indexOf('http') > -1 ? url : process.env.REACT_APP_PUBLISH_PATH + url;
  }

  /**
   * 获取String格式
   * @param value 传入的string或null或undefined
   */
  formatString(value: string | null | undefined) {
    if (value && value.toString().length > 0) {
      return value;
    }
    const nullText = this.nullText;
    return nullText;
  }

  /**
   * 千的格式化
   * @param val
   */
  numberThousandFormat(val: any) {
    var re = /(?=(?!(\b))(\d{3})+$)/g;
    return val.toString().replace(re, ',');
  }

  /**
   * 千分位加逗号
   */
  formatCurrency = num => {
    if (num) {
      num = num.toString().replace(/\$|\,/g, '');
      // if ('' === num || isNaN(num) || '-' === num) { return '-'; }
      var sign = num.indexOf('-') >= 0 ? '-' : '';
      var cents = num.indexOf('.') >= 0 ? num.substr(num.indexOf('.')) : '';
      cents = cents.length > 1 ? cents : '';
      num = num.indexOf('.') > 0 ? num.substring(0, num.indexOf('.')) : num;
      if (sign === '-') {
        num = num.replace(/-/g, '');
      }
      num = num.indexOf('.') > 0 ? num.substring(0, num.indexOf('.')) : num;
      for (var i = 0; i < Math.floor((num.length - (1 + i)) / 3); i++) {
        num =
          num.substring(0, num.length - (4 * i + 3)) +
          ',' +
          num.substring(num.length - (4 * i + 3));
      }
      return sign + num + cents;
    }
    return num;
  };

  /**
   * 判断下载是否成功
   * @param response  接口数据
   * @param onSuccess 成功的回调，自定义，不转的话，方法调用下载
   * @param isFullUrl 是否是全部的路径
   */
  downloadIsSuccess(
    response: any,
    onSuccess?: null | ((response: any) => void),
    isFullUrl?: boolean
  ) {
    const isSuccess = _.get(response, 'is_success', false);
    const failMsg = _.get(response, 'fail_msg', '接口返回值格式错误');

    if (!response) {
      return;
    }

    if (!isSuccess) {
      if (failMsg) {
        ABSMessage.error(failMsg);
      }
      return;
    }

    if (onSuccess) {
      onSuccess(response);
      return;
    }
    // 直接下载文件
    const { data } = response;
    if (!data) {
      ABSMessage.error('暂无数据');
      return;
    }
    if (isFullUrl) {
      this.downloadFile(`${data}`);
      return;
    }
    this.downloadFile(`${GlobalApi.downloadFile}${data}`);
  }

  /**
   * 下载文件
   * @param sUrl
   */
  downloadFile(sUrl: string, name?: string) {
    const isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
    const isSafari = navigator.userAgent.toLowerCase().indexOf('safari') > -1;
    // If in Chrome or Safari - download via virtual link click
    if (isChrome || isSafari) {
      // Creating new link node.
      var link = document.createElement('a');
      link.href = sUrl;

      if (link.download !== undefined) {
        // Set HTML5 download attribute. This will prevent file from opening if supported.
        var fileName = sUrl.substring(sUrl.lastIndexOf('/') + 1, sUrl.length);
        link.download = name || fileName;
      }

      // Dispatching click event.
      if (document.createEvent) {
        var e = document.createEvent('MouseEvents');
        e.initEvent('click', true, true);
        link.dispatchEvent(e);
        return true;
      }
    }

    // Force file download (whether supported by server).
    if (sUrl.indexOf('?') === -1) {
      sUrl += '?download';
    }

    window.open(sUrl, '_self');
    return true;
  }

  /**
   *  将数字转为汉字
   * @param num
   */
  convertToChinaNum(num: number | string) {
    const newNum = Number(num);
    var arr1 = new Array('零', '一', '二', '三', '四', '五', '六', '七', '八', '九');
    if (!newNum || isNaN(newNum)) {
      return arr1[0];
    }
    if (newNum === 0.5) {
      return '半';
    }
    return arr1[newNum];
  }

  /**
   * 限制字数，超出部分以省略号...显示
   * @param {string} txt  原内容
   * @param {number} num  限制字数
   * @returns {string} 新内容
   */
  limitTxtLength(txt: string, num: number) {
    if (txt && txt.length > num) {
      txt = txt.substr(0, num) + '...';
    }
    return txt;
  }

  /**
   * 计算字符的长度
   * @param str
   */
  calcTextLength(str: string) {
    const re = /[\u4e00-\u9fa5]/; // 正则表达式，判断是否为汉字
    let count = 0;
    for (let i = 0; i < str.length; i++) {
      count += re.test(str.charAt(i)) ? 2 : 1;
    }
    return count;
  }

  /**
   * 使滚动容器的滚动条出现
   * @param container 滚动条所在的容器
   */
  setScrollContainerActive(container: HTMLElement) {
    setTimeout(() => {
      container.scrollLeft = 1;
    }, 500);
  }

  /**
   * 返回提示的信息（Form）
   * @param message 错误信息
   */
  getValidate(message: string) {
    if (!message) {
      return {
        validateStatus: 'success',
        errorMsg: ''
      };
    }
    return {
      validateStatus: 'error',
      errorMsg: `${message}`
    };
  }

  /**
   * 获取IE的版本
   */
  getIEVersion() {
    var userAgent = userAgent || navigator.userAgent; // 取得浏览器的userAgent字符串
    const isIE = userAgent.indexOf('compatible') > -1 && userAgent.indexOf('MSIE') > -1; // 判断是否IE<11浏览器
    const isEdge = userAgent.indexOf('Edge') > -1 && !isIE; // 判断是否IE的Edge浏览器
    const isIE11 = userAgent.indexOf('Trident') > -1 && userAgent.indexOf('rv:11.0') > -1;
    if (isIE) {
      const fIEVersion = parseFloat(userAgent.match(/MSIE (\d+\.\d+)/i)[1]);
      return fIEVersion;
    } else if (isEdge) {
      return 'edge'; // edge
    } else if (isIE11) {
      return 11; // IE11
    } else {
      return -1; // 不是ie浏览器
    }
  }

  /**
   * 涨跌幅类型数据需要添加‘+’‘-’（正数需要添加‘+’，负数不需要）
   * @param key
   * @param data
   */
  addSymbol(data: number, text: string | number) {
    let symbol = '';
    if (data && Number(data) > 0) {
      symbol = '+';
    }
    return symbol + text;
  }

  /**
   * 弹出框显示与隐藏
   * @param modal
   * @param visible
   * @param params
   */
  modalShow(modal: any, visible: boolean, params?: any) {
    if (modal) {
      modal.show(visible, params);
    }
  }

  /**
   * 生成Guid
   */
  guid() {
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function(c: any) {
      // tslint:disable-next-line:no-bitwise
      const r = (Math.random() * 16) | 0,
        // tslint:disable-next-line:no-bitwise
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * 通过图片的文件服务器预览地址切取fileCode
   * @param url
   */
  getCodeFromUrl(url: string) {
    const fileCode = url.split('filestore/common/downloadimg/cnabs/')[1].split('/')[0];
    return fileCode;
  }

  // 分类
  groupBy(arr: any[], type: string, formatFun: (item: any) => any) {
    if (formatFun && typeof formatFun !== 'function') {
      return;
    }
    const typeObj = arr.reduce((obj, item) => {
      if (obj[item[type]]) {
        obj[item[type]].push(formatFun ? formatFun(item) : item);
      } else {
        obj[item[type]] = [];
        obj[item[type]].push(formatFun ? formatFun(item) : item);
      }
      return obj;
    }, {});
    let list: any[] = [];
    // tslint:disable-next-line:forin
    for (const key in typeObj) {
      list.push({ name: key as string, data: typeObj[key] });
    }
  }

  // 获取url中的参数
  getUrlParams(name?: string) {
    // 不传name返回所有值，否则返回对应值
    let url: any = window.location.href;
    let num = url.indexOf('?');
    if (num < 0) {
      return;
    }
    url = url.substr(num + 1);
    url = url.split('&');
    var nameres;
    var obj = {};
    // 获取全部参数及其值
    for (let i = 0; i < url.length; i++) {
      var info = url[i].split('=');
      obj[info[0]] = decodeURI(info[1]);
    }
    // 如果传入一个参数名称，就匹配其值
    if (name) {
      nameres = obj[name];
    } else {
      nameres = obj;
    }
    // 返回结果
    return nameres;
  }
// 对数字num截取digits位小数，不四舍五入
  toFixedDown(num: number, digits: number) {
    let re = new RegExp('(\\d+\\.\\d{' + digits + '})(\\d)'),
        m = num.toString().match(re);
    return m ? parseFloat(m[1]) : num.valueOf();
  }
}

export default new CommonUtils();



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/commonUtils.ts